
import { Achievement } from '../hooks/types';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Create a function to check if an achievement popup has been shown
export const hasShownAchievementPopup = (achievementId: string): boolean => {
  const shownAchievements = localStorage.getItem('shownAchievementPopups');
  if (!shownAchievements) return false;
  
  try {
    const achievementIds = JSON.parse(shownAchievements);
    return achievementIds.includes(achievementId);
  } catch (e) {
    console.error('Error parsing shown achievements:', e);
    return false;
  }
};

// Create a function to mark an achievement popup as shown
export const markAchievementPopupAsShown = (achievementId: string): void => {
  const shownAchievements = localStorage.getItem('shownAchievementPopups');
  let achievementIds: string[] = [];
  
  try {
    if (shownAchievements) {
      achievementIds = JSON.parse(shownAchievements);
    }
    
    if (!achievementIds.includes(achievementId)) {
      achievementIds.push(achievementId);
      localStorage.setItem('shownAchievementPopups', JSON.stringify(achievementIds));
    }
  } catch (e) {
    console.error('Error updating shown achievements:', e);
  }
};

export const saveEarnedAchievement = async (userId: string, achievementId: string, xpReward: number = 0) => {
  if (!userId) {
    console.error('No user ID provided, cannot save achievement');
    return;
  }
  
  console.log(`Saving achievement ${achievementId} for user ${userId} with ${xpReward} XP`);
  
  try {
    // First check if this achievement is already earned to avoid duplicates
    const { data: existingAchievement, error: checkError } = await supabase
      .from('user_achievements')
      .select('id')
      .eq('user_id', userId)
      .eq('achievement_id', achievementId)
      .maybeSingle();
      
    if (checkError) {
      console.error('Error checking for existing achievement:', checkError);
      return;
    }
      
    if (existingAchievement) {
      // Already earned, no need to save again
      console.log('Achievement already earned, skipping save');
      return;
    }
    
    console.log('Achievement not previously earned, saving now');
    
    const { error } = await supabase
      .from('user_achievements')
      .insert({
        user_id: userId,
        achievement_id: achievementId,
        earned_at: new Date().toISOString()
      });
    
    if (error) {
      console.error('Error saving achievement:', error);
      throw error;
    }
    
    console.log('Achievement saved successfully');
    
    // If there's an XP reward, update the user's experience
    if (xpReward > 0) {
      console.log(`Awarding ${xpReward} XP to user`);
      
      // First check if user has an experience record
      const { data: userXp, error: xpError } = await supabase
        .from('user_experience')
        .select('total_xp')
        .eq('user_id', userId)
        .maybeSingle();
        
      if (xpError) {
        console.error('Error fetching user experience:', xpError);
      } else if (!userXp) {
        // No record found, create one
        console.log('No experience record found, creating new one');
        await supabase
          .from('user_experience')
          .insert({ 
            user_id: userId, 
            total_xp: xpReward,
            updated_at: new Date().toISOString() 
          });
          
        // Check for XP-based card drops after XP creation
        try {
          const { triggerXPMilestoneCheck, triggerXPIntervalCheck } = await import('../../../services/card-service');
          await Promise.all([
            triggerXPMilestoneCheck(userId, xpReward),
            triggerXPIntervalCheck(userId, xpReward)
          ]);
        } catch (cardError) {
          console.error('Error checking XP-based card drops:', cardError);
        }
      } else {
        // Update existing record
        const newTotalXp = userXp.total_xp + xpReward;
        console.log(`Updating XP from ${userXp.total_xp} to ${newTotalXp}`);
        
        await supabase
          .from('user_experience')
          .update({ 
            total_xp: newTotalXp, 
            updated_at: new Date().toISOString() 
          })
          .eq('user_id', userId);
          
        // Check for XP-based card drops after XP update
        try {
          const { triggerXPMilestoneCheck, triggerXPIntervalCheck } = await import('../../../services/card-service');
          await Promise.all([
            triggerXPMilestoneCheck(userId, newTotalXp),
            triggerXPIntervalCheck(userId, newTotalXp)
          ]);
        } catch (cardError) {
          console.error('Error checking XP-based card drops:', cardError);
        }
      }
    }
    
    // Mark this achievement as having shown a popup
    markAchievementPopupAsShown(achievementId);
    
    toast({
      title: 'Achievement Unlocked!',
      description: `You've earned a new achievement${xpReward ? ` and ${xpReward} XP!` : '!'}`,
      variant: 'default'
    });
    
  } catch (error) {
    console.error('Error in saveEarnedAchievement:', error);
  }
};

export const groupAchievementsByType = (achievements: Achievement[]) => {
  if (!achievements?.length) return {};
  
  return achievements.reduce((acc, achievement) => {
    const type = achievement.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(achievement);
    return acc;
  }, {} as Record<string, Achievement[]>);
};
